import { useEffect, useMemo } from 'react';
import { useCalendarSharingStore } from '../../../lib/stores/calendarSharingStore';
import { useSettingsStore } from '../../../lib/stores/settingsStore';
import { useDayBusy } from '../../../lib/calendar/team/useDayBusy';
import { useEnsureSharing } from '../../../lib/calendar/team/useEnsureSharing';
import { firstFreeStart, formatMinutes, mergeIntervals, overlapsWith, parseTime } from '../../../lib/calendar/team/availability';
import { blocksFor, gridRange, workWindowFor } from '../../../lib/calendar/team/workHours';
import { unavailabilityBlocksFor } from '../../../lib/calendar/team/unavailability';
import { useCalendarUnavailabilityStore } from '../../../lib/stores/calendarUnavailabilityStore';
import { useUserData } from '../../../lib/auth';

interface Props {
  assigneeId: string | null;
  assigneeName?: string | null;
  /** AAAA-MM-GG */
  date: string;
  /** HH:MM */
  time: string;
  /** minuti; il modulo non ha una durata, si conta un'ora */
  durationMin?: number;
  /** la task che si sta modificando: non deve contare come impegno */
  excludeActivityId?: string | null;
  onUseTime: (hhmm: string) => void;
}

/**
 * Sotto data e ora: la giornata della persona scelta (impegni e orario scelto) e, se si sovrappone,
 * l'avviso con il primo orario libero. Non blocca mai il salvataggio.
 */
export function AssigneeAvailabilityHint({ assigneeId, assigneeName, date, time, durationMin = 60, excludeActivityId, onUseTime }: Props) {
  useEnsureSharing();
  const level = useCalendarSharingStore(s => (assigneeId ? s.visible[assigneeId] : undefined));
  const { busy: tasks } = useDayBusy(level ? assigneeId : null, date, excludeActivityId);
  const availability = useSettingsStore(s => (s.settings as any)?.calendar_settings?.availability);
  const start = parseTime(time);
  const day = useMemo(() => new Date(`${date}T00:00:00`), [date]);
  // Impegni + blocchi ricorrenti (pausa pranzo, riunione fissa) + indisponibilità con date (ferie, fuori sede)
  const valid = !Number.isNaN(day.getTime());
  const { organizationId } = useUserData();
  const unavailabilityRows = useCalendarUnavailabilityStore(s => s.rows);
  const fetchUnavailability = useCalendarUnavailabilityStore(s => s.fetchRange);
  useEffect(() => {
    if (!valid || !organizationId || !assigneeId || !level) return;
    const next = new Date(day); next.setDate(next.getDate() + 1);
    void fetchUnavailability(organizationId, day, next);
  }, [valid, day, organizationId, assigneeId, level, fetchUnavailability]);
  const blocks = useMemo(
    () => (valid ? [...blocksFor(day, availability, assigneeId), ...unavailabilityBlocksFor(day, Object.values(unavailabilityRows), assigneeId)].sort((a, b) => a.start - b.start) : []),
    [valid, day, availability, assigneeId, unavailabilityRows],
  );
  const busy = useMemo(() => mergeIntervals([...tasks, ...blocks]), [tasks, blocks]);
  const work = valid ? workWindowFor(day, availability, assigneeId) : null;
  const { start: FROM, end: TO } = gridRange([work]);
  const pct = (m: number) => `${Math.max(0, Math.min(100, ((m - FROM) / (TO - FROM)) * 100))}%`;

  if (!assigneeId || !level || Number.isNaN(start) || Number.isNaN(day.getTime())) return null;

  const end = start + durationMin;
  const clash = overlapsWith(busy, start, end);
  // Fuori orario si avvisa solo se l'azienda ha impostato gli orari: senza, resta come prima
  const configured = !!(availability?.default || (assigneeId && availability?.people?.[assigneeId]?.hours));
  const outside = configured && (!work || start < work.start || end > work.end);
  const first = clash.length || outside ? firstFreeStart(busy, work, start, durationMin) ?? firstFreeStart(busy, work, 0, durationMin) : null;
  const blockAt = blocks.find(b => b.start < end && b.end > start);
  const name = (assigneeName || 'La persona').split(' ')[0];

  return (
    <div className="mt-2 border-t border-[rgba(55,53,47,0.09)] px-1.5 pt-2 dark:border-white/10" data-testid="assignee-availability">
      <div className="relative h-3 overflow-hidden rounded-sm bg-[rgba(55,53,47,0.04)] dark:bg-white/5" aria-hidden>
        {busy.map((b, i) => (
          <div key={i} className="absolute inset-y-0 bg-[rgba(55,53,47,0.2)]" style={{ left: pct(b.start), width: `calc(${pct(b.end)} - ${pct(b.start)})` }} />
        ))}
        <div
          className={`absolute inset-y-0 border ${clash.length || outside ? 'border-[#E03E3E] bg-[#E03E3E]/15' : 'border-[#37352F] bg-[#37352F]/10'}`}
          style={{ left: pct(start), width: `calc(${pct(Math.min(end, TO))} - ${pct(start)})` }}
        />
      </div>
      <div className="mt-0.5 flex justify-between text-[10px] text-[#787774]" aria-hidden>
        <span>{formatMinutes(FROM)}</span><span>{formatMinutes(Math.round((FROM + TO) / 120) * 60)}</span><span>{formatMinutes(TO)}</span>
      </div>
      {clash.length > 0 || outside ? (
        <p className="mt-1 text-[13px] text-[#37352F] dark:text-white/90">
          {outside && !clash.length
            ? (work ? `${name} lavora dalle ${formatMinutes(work.start)} alle ${formatMinutes(work.end)}.` : `${name} in questo giorno non lavora.`)
            : blockAt && blockAt.start <= 0 && blockAt.end >= 24 * 60
              ? `${name} non è disponibile in questa giornata (${blockAt.title}).`
            : blockAt
              ? `${name} non è disponibile dalle ${formatMinutes(blockAt.start)} alle ${formatMinutes(blockAt.end)} (${blockAt.title}).`
              : `${name} ha già un impegno ${clash.map(c => `dalle ${formatMinutes(c.start)} alle ${formatMinutes(c.end)}`).join(' e ')}.`}
          {first !== null ? ` Il primo orario libero è alle ${formatMinutes(first)}` : ' Non ha altri orari liberi in questa giornata'}
          {first !== null && (
            <button
              type="button"
              onClick={() => onUseTime(`${String(Math.floor(first / 60)).padStart(2, '0')}:${String(first % 60).padStart(2, '0')}`)}
              className="ml-2 rounded px-1.5 py-0.5 text-[12px] font-medium text-[#37352F] underline decoration-[rgba(55,53,47,0.3)] hover:bg-[rgba(55,53,47,0.08)] dark:text-white"
            >
              Usa le {formatMinutes(first)}
            </button>
          )}
        </p>
      ) : (
        <p className="mt-1 text-[13px] text-[#787774]">{name} non ha impegni a quell'ora.</p>
      )}
    </div>
  );
}
